import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type UrlParamValue = string | number | null | undefined;
export type UrlParamPatch = Record<string, UrlParamValue>;

export interface SetUrlParamsOptions {
	/** Replace the current history entry instead of pushing a new one. */
	replace?: boolean;
}

function applyPatch(prev: URLSearchParams, patch: UrlParamPatch): URLSearchParams {
	const next = new URLSearchParams(prev);
	for (const [key, value] of Object.entries(patch)) {
		if (value === null || value === undefined || value === "") {
			next.delete(key);
		} else {
			next.set(key, String(value));
		}
	}
	return next;
}

/**
 * Read/write URL search params as the single source of truth for UI state
 * (pagination, filters, tabs, drill-down selections) so browser back/forward
 * restores every step. One `setParams` call = one history entry, so combined
 * changes (e.g. filter change + page reset) must go through a single patch.
 * Empty string / null / undefined removes the key, keeping URLs clean.
 */
export function useUrlParams() {
	const [searchParams, setSearchParams] = useSearchParams();

	const setParams = useCallback(
		(patch: UrlParamPatch, options?: SetUrlParamsOptions) => {
			setSearchParams((prev) => applyPatch(prev, patch), { replace: options?.replace });
		},
		[setSearchParams]
	);

	return { searchParams, setParams };
}

/**
 * Single URL-backed value (tab key, selected entity id...). Setting the
 * default value removes the param from the URL.
 */
export function useUrlParam(
	key: string,
	defaultValue?: string
): [string | undefined, (value: UrlParamValue, options?: SetUrlParamsOptions) => void] {
	const { searchParams, setParams } = useUrlParams();
	const value = searchParams.get(key) ?? defaultValue;

	const setValue = useCallback(
		(next: UrlParamValue, options?: SetUrlParamsOptions) => {
			setParams({ [key]: next === defaultValue ? null : next }, options);
		},
		[key, defaultValue, setParams]
	);

	return [value, setValue];
}

function parsePositiveInt(raw: string | null): number | undefined {
	if (!raw) {
		return undefined;
	}
	const parsed = Number.parseInt(raw, 10);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export interface UrlTableStateOptions {
	/**
	 * Namespace for this table's params (e.g. "staff" → `staffPage`). Required
	 * when several tables can render on one route (org detail tabs) so their
	 * pagination params don't collide.
	 */
	prefix?: string;
	defaultLimit?: number;
}

/**
 * URL-backed table state: pagination plus string filters. Defaults (page 1,
 * default limit) are kept out of the URL. `setFilters` resets the page in the
 * same history entry.
 */
export function useUrlTableState(options?: UrlTableStateOptions) {
	const { searchParams, setParams } = useUrlParams();
	const prefix = options?.prefix ?? "";
	const defaultLimit = options?.defaultLimit ?? 10;

	const withPrefix = useCallback(
		(key: string) => (prefix ? `${prefix}${key.charAt(0).toUpperCase()}${key.slice(1)}` : key),
		[prefix]
	);

	const pageKey = withPrefix("page");
	const limitKey = withPrefix("limit");

	const page = parsePositiveInt(searchParams.get(pageKey)) ?? 1;
	const limit = parsePositiveInt(searchParams.get(limitKey)) ?? defaultLimit;

	const setPage = useCallback(
		(nextPage: number, nextLimit?: number) => {
			const patch: UrlParamPatch = { [pageKey]: nextPage > 1 ? nextPage : null };
			if (nextLimit !== undefined) {
				patch[limitKey] = nextLimit !== defaultLimit ? nextLimit : null;
			}
			setParams(patch);
		},
		[pageKey, limitKey, defaultLimit, setParams]
	);

	const getFilter = useCallback(
		(key: string) => searchParams.get(withPrefix(key)) ?? undefined,
		[searchParams, withPrefix]
	);

	/** Applies filter changes and resets pagination in one history entry. */
	const setFilters = useCallback(
		(patch: UrlParamPatch, options?: SetUrlParamsOptions) => {
			const prefixed: UrlParamPatch = { [pageKey]: null };
			for (const [key, value] of Object.entries(patch)) {
				prefixed[withPrefix(key)] = value;
			}
			setParams(prefixed, options);
		},
		[pageKey, withPrefix, setParams]
	);

	return useMemo(
		() => ({ page, limit, setPage, getFilter, setFilters }),
		[page, limit, setPage, getFilter, setFilters]
	);
}
